import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { client, unwrap } from '../../api/client';
import { ApiError } from '../../api/errors';
import { meQueryKey, useAuth } from '../../auth/AuthProvider';
import { CURRENCY_OPTIONS } from '../../lib/currencies';
import type { Session } from '../../auth/tokens';
import {
  Button,
  Check,
  ErrorBox,
  SelectField,
  TextField,
  Wordmark,
  fieldErrors,
} from '../../ui/ui';

/** Panel lateral de las pantallas de acceso: el negocio contado como una conversación. */
function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="auth-page">
      <aside className="auth-aside">
        <div className="auth-mesh" aria-hidden />
        <div className="brand">
          <Wordmark />
        </div>
        <div>
          <p className="eyebrow">ERP para comercio conversacional</p>
          <h1>
            Tus ventas empiezan en un chat. <em>Tu inventario lo sabe.</em>
          </h1>
        </div>
        <div className="thread" aria-hidden>
          <div className="bubble">
            <div className="who">Cliente · WhatsApp</div>
            <div className="what">¿Tienen los tenis negros en talla 42?</div>
            <time>10:41</time>
          </div>
          <div className="bubble out">
            <div className="who">EATHERIX</div>
            <div className="what">Sí, quedan 3. Te aparté 1 par por 24 h.</div>
            <time>10:41</time>
          </div>
          <div className="bubble out">
            <div className="who">EATHERIX</div>
            <div className="what">Pedido SO-000184 · Paga aquí →</div>
            <time>10:42</time>
          </div>
        </div>
        <small className="auth-foot">CRM · Inbox de WhatsApp · Inventario</small>
      </aside>
      <div className="auth-form">{children}</div>
    </div>
  );
}

export function LoginPage() {
  const { login, switchTenant, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState<Session | null>(null);
  const [needsCode, setNeedsCode] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const target = (location.state as { from?: string } | null)?.from ?? '/';

  if (status === 'authenticated' && !picker) return <Navigate to={target} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await login(email, password, needsCode ? mfaCode : undefined);
      if (session.tenants.length > 1) setPicker(session);
      else navigate(target, { replace: true });
    } catch (e) {
      // Con verificación en dos pasos la API pide el código antes de emitir la sesión.
      if (e instanceof ApiError && e.code === 'auth.mfa_required') {
        setNeedsCode(true);
        return;
      }
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  if (picker) {
    return (
      <AuthLayout>
        <div className="stack" style={{ width: 'min(380px, 100%)' }}>
          <h2>¿A qué empresa quieres entrar?</h2>
          {picker.tenants.map((t) => (
            <Button
              key={t.id}
              variant={t.id === picker.tenantId ? 'primary' : 'default'}
              onClick={async () => {
                if (t.id !== picker.tenantId) await switchTenant(t.id);
                setPicker(null);
                navigate(target, { replace: true });
              }}
            >
              {t.name} <span className="muted">· {t.role}</span>
            </Button>
          ))}
        </div>
      </AuthLayout>
    );
  }

  const errors = fieldErrors(error);
  return (
    <AuthLayout>
      <form onSubmit={submit} noValidate>
        <h2>Ingresar</h2>
        {error !== null && Object.keys(errors).length === 0 && <ErrorBox error={error} />}
        <TextField
          label="Correo"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
        />
        <TextField
          label="Contraseña"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
        />
        <Link to="/forgot-password" className="auth-forgot">
          ¿Olvidaste tu contraseña?
        </Link>
        {needsCode && (
          <TextField
            label="Código de verificación"
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
            required
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            help="Los 6 dígitos de tu app de autenticación, o un código de recuperación"
          />
        )}
        <Button type="submit" variant="primary" busy={busy} disabled={needsCode && !mfaCode.trim()}>
          {needsCode ? 'Verificar' : 'Ingresar'}
        </Button>
        <p className="muted">
          ¿Aún no tienes cuenta? <Link to="/signup">Crea tu empresa gratis</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export function SignupPage() {
  const { signup, status } = useAuth();
  const navigate = useNavigate();
  // Plan elegido en el sitio (eatherix.com/sales): la prueba es de ese plan.
  const [params] = useSearchParams();
  // Un id anterior también sirve: el servidor lo lleva a su reemplazo.
  const planId = params.get('plan')?.trim() || undefined;
  const catalog = useQuery({
    queryKey: ['public-plans'],
    queryFn: () => unwrap(client.GET('/v1/public/plans')),
    staleTime: 5 * 60_000,
  });
  const chosen = catalog.data?.plans.find((plan) => plan.id === planId);
  const planName = chosen?.name;
  // Días de prueba del catálogo (los del plan elegido o los del plan de prueba por defecto); se
  // editan en la consola.
  const trialPlan = chosen ?? catalog.data?.plans.find((plan) => plan.isTrialDefault);
  const trialDays = trialPlan?.trialDays ?? null;
  const trialText = trialDays ? `${trialDays} días` : 'Prueba';
  const [form, setForm] = useState({
    companyName: '',
    fullName: '',
    email: '',
    password: '',
    country: 'CO',
    currency: 'COP',
  });
  const [sampleData, setSampleData] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  if (status === 'authenticated') return <Navigate to="/" replace />;
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const errors = fieldErrors(error);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const input = { ...form, timezone: 'America/Bogota', sampleData };
      try {
        await signup({ ...input, ...(planId ? { planId } : {}) });
      } catch (e) {
        // Un enlace viejo del sitio con un plan que ya no existe no debe impedir el registro:
        // la prueba queda con el plan por defecto.
        if (!(planId && e instanceof ApiError && e.code === 'billing.plan_unavailable')) throw e;
        await signup(input);
      }
      navigate('/', { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={submit} noValidate>
        <h2>Crea tu empresa</h2>
        <p className="muted" style={{ margin: 0 }}>
          {planName
            ? `${trialText} del plan ${planName}, sin tarjeta.`
            : `${trialText} gratis, sin tarjeta.`}
        </p>
        {error !== null && Object.keys(errors).length === 0 && <ErrorBox error={error} />}
        <TextField
          label="Nombre de la empresa"
          required
          value={form.companyName}
          onChange={set('companyName')}
          error={errors.companyName}
        />
        <TextField
          label="Tu nombre"
          required
          value={form.fullName}
          onChange={set('fullName')}
          error={errors.fullName}
        />
        <TextField
          label="Correo"
          type="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={set('email')}
          error={errors.email}
        />
        <TextField
          label="Contraseña"
          type="password"
          autoComplete="new-password"
          help="Mínimo 10 caracteres con mayúsculas, minúsculas y números"
          required
          value={form.password}
          onChange={set('password')}
          error={errors.password}
        />
        <div className="row">
          <SelectField
            label="País"
            value={form.country}
            onChange={(e) => {
              const country = e.target.value;
              const currency = country === 'CO' ? 'COP' : 'USD';
              setForm((f) => ({ ...f, country, currency }));
            }}
            options={[
              { value: 'CO', label: 'Colombia' },
              { value: 'MX', label: 'México' },
              { value: 'PE', label: 'Perú' },
              { value: 'CL', label: 'Chile' },
            ]}
          />
          <SelectField
            label="Moneda"
            value={form.currency}
            onChange={set('currency')}
            options={[...CURRENCY_OPTIONS]}
            help="Se puede cambiar hasta registrar el primer documento"
          />
        </div>
        <Check
          label="Cargar productos y clientes de ejemplo para explorar (los borras con un clic)"
          checked={sampleData}
          onChange={(e) => setSampleData(e.target.checked)}
        />
        <Button type="submit" variant="primary" busy={busy}>
          Crear empresa
        </Button>
        <p className="muted">
          ¿Ya tienes cuenta? <Link to="/login">Ingresa</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const { acceptSession } = useAuth();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const errors = fieldErrors(error);
  // A qué empresa y con qué rol invitan, y si la persona ya tiene cuenta (pide solo lo necesario).
  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => unwrap(client.GET('/v1/auth/invitation', { params: { query: { token } } })),
    enabled: token.length >= 20,
    retry: false,
  });
  const invite = preview.data;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await unwrap<Session>(
        client.POST('/v1/auth/accept-invitation', {
          body: {
            token,
            password,
            ...(fullName ? { fullName } : {}),
            ...(needsCode ? { mfaCode } : {}),
          },
        }),
      );
      acceptSession(session);
      navigate('/', { replace: true });
    } catch (e) {
      // Una cuenta existente con verificación en dos pasos también debe dar su código.
      if (e instanceof ApiError && e.code === 'auth.mfa_required') {
        setNeedsCode(true);
        return;
      }
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={submit} noValidate>
        <h2>{invite ? `Únete a ${invite.tenantName}` : 'Únete a tu equipo'}</h2>
        {invite && (
          <p className="muted" style={{ marginTop: 0 }}>
            Te invitaron como <strong>{invite.roleName}</strong> con el correo {invite.email}.
            {invite.existingUser
              ? ' Ya tienes cuenta: entra con tu contraseña actual.'
              : ' Crea tu cuenta con tu nombre y una contraseña.'}
          </p>
        )}
        {(!token || preview.isError) && (
          <div className="alert">
            El enlace de invitación no es válido, ya se usó o venció. Pide a tu administrador uno
            nuevo.
          </div>
        )}
        {error !== null && Object.keys(errors).length === 0 && <ErrorBox error={error} />}
        {!invite?.existingUser && (
          <TextField
            label="Tu nombre"
            help={invite ? undefined : 'Déjalo vacío si ya tienes cuenta en EATHERIX'}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            error={errors.fullName}
          />
        )}
        <TextField
          label={invite?.existingUser ? 'Tu contraseña actual' : 'Contraseña'}
          type="password"
          help={
            invite
              ? invite.existingUser
                ? undefined
                : 'Mínimo 10 caracteres con mayúsculas, minúsculas y números'
              : 'Si ya tienes cuenta, usa tu contraseña actual'
          }
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
        />
        {needsCode && (
          <TextField
            label="Código de verificación"
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
            required
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            help="Los 6 dígitos de tu app de autenticación, o un código de recuperación"
          />
        )}
        <Button
          type="submit"
          variant="primary"
          busy={busy}
          disabled={!token || (needsCode && !mfaCode.trim())}
        >
          Aceptar invitación
        </Button>
      </form>
    </AuthLayout>
  );
}

/** Solicitud del enlace para restablecer la contraseña (no revela si la cuenta existe). */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await unwrap(client.POST('/v1/auth/forgot-password', { body: { email: email.trim() } }));
      setSent(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const errors = fieldErrors(error);
  return (
    <AuthLayout>
      {sent ? (
        <div className="stack" style={{ width: 'min(380px, 100%)' }}>
          <h2>Revisa tu correo</h2>
          <p className="muted">
            Si {email.trim()} tiene una cuenta, te enviamos un enlace para crear una nueva
            contraseña. Vence en 1 hora.
          </p>
          <Link to="/login">Volver a ingresar</Link>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <h2>Restablecer contraseña</h2>
          <p className="muted">Te enviaremos un enlace a tu correo.</p>
          {error !== null && Object.keys(errors).length === 0 && <ErrorBox error={error} />}
          <TextField
            label="Correo"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
          />
          <Button type="submit" variant="primary" busy={busy} disabled={!email.trim()}>
            Enviar enlace
          </Button>
          <p className="muted">
            <Link to="/login">Volver a ingresar</Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}

/** Nueva contraseña con el enlace recibido por correo. */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await unwrap(
        client.POST('/v1/auth/reset-password', { body: { token, newPassword: password } }),
      );
      setDone(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const errors = fieldErrors(error);
  if (!token || done)
    return (
      <AuthLayout>
        <div className="stack" style={{ width: 'min(380px, 100%)' }}>
          <h2>{done ? 'Contraseña actualizada' : 'Enlace incompleto'}</h2>
          <p className="muted">
            {done
              ? 'Ya puedes ingresar con tu nueva contraseña. Cerramos las demás sesiones abiertas.'
              : 'Abre el enlace completo que te enviamos por correo o solicita uno nuevo.'}
          </p>
          <Button variant="primary" onClick={() => navigate(done ? '/login' : '/forgot-password')}>
            {done ? 'Ingresar' : 'Solicitar enlace'}
          </Button>
        </div>
      </AuthLayout>
    );
  return (
    <AuthLayout>
      <form onSubmit={submit} noValidate>
        <h2>Nueva contraseña</h2>
        {error !== null && Object.keys(errors).length === 0 && <ErrorBox error={error} />}
        <TextField
          label="Nueva contraseña"
          type="password"
          autoComplete="new-password"
          required
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.newPassword}
          help="Mínimo 10 caracteres, con mayúsculas, minúsculas y números"
        />
        <TextField
          label="Confirmar contraseña"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={mismatch ? 'Las contraseñas no coinciden' : undefined}
        />
        <Button type="submit" variant="primary" busy={busy} disabled={!password || mismatch}>
          Guardar contraseña
        </Button>
        <p className="muted">
          <Link to="/forgot-password">Solicitar otro enlace</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

/** Confirmación del correo con el enlace enviado al registrarse. */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'verifying' | 'done' | 'failed'>(
    token ? 'verifying' : 'failed',
  );
  const [error, setError] = useState<unknown>(null);
  // El enlace es de un solo uso: en modo estricto React monta dos veces y no debe gastarse antes.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    unwrap(client.POST('/v1/auth/verify-email', { body: { token } }))
      .then(() => {
        setState('done');
        void queryClient.invalidateQueries({ queryKey: meQueryKey });
      })
      .catch((e: unknown) => {
        setError(e);
        setState('failed');
      });
  }, [token, queryClient]);

  return (
    <AuthLayout>
      <div className="stack" style={{ width: 'min(380px, 100%)' }}>
        <h2>
          {state === 'verifying'
            ? 'Confirmando tu correo…'
            : state === 'done'
              ? 'Correo confirmado'
              : 'No pudimos confirmar el correo'}
        </h2>
        {state === 'done' && (
          <p className="muted">
            Listo. Te enviamos la bienvenida y desde ahora los avisos de tu cuenta llegan a este
            correo.
          </p>
        )}
        {state === 'failed' &&
          (error !== null ? (
            <ErrorBox error={error} />
          ) : (
            <p className="muted">Abre el enlace completo que te enviamos por correo.</p>
          ))}
        {state === 'failed' && (
          <p className="muted">
            Puedes pedir un enlace nuevo desde el aviso en la parte superior del panel.
          </p>
        )}
        {state !== 'verifying' && (
          <Button variant="primary" onClick={() => navigate('/')}>
            Ir al panel
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}
